import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/format";
import {
  aendereMerosKlappFarbe,
  bedarfAnstehend,
  erfasseMerosZustand,
  getMerosUebersicht,
  legeMerosKlappTermin,
  loescheMerosKlappTermin,
  merosZustandLabel,
  speichereMerosFarben,
  type MerosBedarf,
  type MerosUebersicht,
  type MerosZustand,
} from "@/lib/meros";
import { PLANUNG_COLORS } from "@/lib/montageplanung";
import { toast } from "@/lib/toast";
import { MerosZustandBadge } from "@/pages/veranstaltungen/MerosZustandBadge";

const FARBE_OBEN = "#0a84ff";
const FARBE_UNTEN = "#8e8e93";

function FarbeWahl({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (color: string) => void;
}) {
  return (
    <select
      className="eh-input h-8 w-28"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      {PLANUNG_COLORS.map((color) => (
        <option key={color.value} value={color.value}>
          {color.label}
        </option>
      ))}
    </select>
  );
}

function jetztLokal(): string {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function ortZeile(item: MerosBedarf): string {
  const parts = [item.bereich, item.ort, item.venue]
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.filter((part, index) => parts.indexOf(part) === index).join(" · ");
}

function BedarfListe({
  items,
  aktuell,
}: {
  items: MerosBedarf[];
  aktuell: MerosZustand | null;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-label-secondary">Keine Einträge.</p>;
  }
  return (
    <ul className="divide-y divide-separator">
      {items.map((item, index) => {
        const abweichung = Boolean(aktuell && item.zustand && item.zustand !== aktuell);
        return (
          <li key={`${item.mail_id}-${item.datum}-${index}`} className="py-3 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-label">{item.event_title || "Veranstaltung"}</p>
                <p className="mt-0.5 text-sm text-label-secondary">
                  {[item.datum || item.event_date, ortZeile(item), item.kategorie].filter(Boolean).join(" · ")}
                </p>
                {item.aufgabe ? <p className="mt-1 text-sm text-label">{item.aufgabe}</p> : null}
                {item.notiz ? (
                  <p className="mt-1 whitespace-pre-line text-xs leading-5 text-label-secondary">{item.notiz}</p>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-1">
                <MerosZustandBadge zustand={item.zustand} />
                {!item.zustand ? (
                  <span className="text-xs text-label-tertiary">Richtung unklar</span>
                ) : null}
                {abweichung ? (
                  <span className="text-xs font-medium text-status-orange">
                    Weicht von {merosZustandLabel(aktuell)} ab
                  </span>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function MerosPage() {
  const [data, setData] = useState<MerosUebersicht | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zustand, setZustand] = useState<MerosZustand | "">("");
  const [erfasstAm, setErfasstAm] = useState(jetztLokal);
  const [notiz, setNotiz] = useState("");
  const [klappAm, setKlappAm] = useState(jetztLokal);
  const [klappNotiz, setKlappNotiz] = useState("");
  const [klappFarbe, setKlappFarbe] = useState(FARBE_UNTEN);
  const [klappSaving, setKlappSaving] = useState(false);
  const naechsteFarbe =
    data?.aktuell?.zustand === "oben"
      ? data?.farben?.unten || FARBE_UNTEN
      : data?.farben?.oben || FARBE_OBEN;

  useEffect(() => {
    setKlappFarbe(naechsteFarbe);
  }, [naechsteFarbe]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getMerosUebersicht());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Der Merostützen-Zustand konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const anstehend = useMemo(
    () => (data?.bedarf || []).filter((item) => bedarfAnstehend(item)),
    [data],
  );
  const vergangen = useMemo(
    () => (data?.bedarf || []).filter((item) => !bedarfAnstehend(item)).reverse(),
    [data],
  );
  const aktuell = data?.aktuell?.zustand || null;
  const naechsterKlapp = (data?.klapp_termine || []).find(
    (termin) => new Date(termin.klapp_am).getTime() >= Date.now(),
  );

  async function handleSave() {
    if (zustand !== "oben" && zustand !== "unten") return;
    const when = new Date(erfasstAm);
    if (Number.isNaN(when.getTime())) {
      toast.error("Bitte einen gültigen Zeitpunkt angeben.");
      return;
    }
    setSaving(true);
    try {
      const note = notiz.trim();
      const next = await erfasseMerosZustand({
        zustand,
        erfasst_am: when.toISOString(),
        notiz: note || undefined,
      });
      setData(next);
      setNotiz("");
      setErfasstAm(jetztLokal());
      setError(null);
      toast.success(`Zustand ${merosZustandLabel(zustand)} erfasst.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Der Zustand konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  async function handleKlapp() {
    const when = new Date(klappAm);
    if (Number.isNaN(when.getTime())) {
      toast.error("Bitte einen gültigen Zeitpunkt angeben.");
      return;
    }
    setKlappSaving(true);
    try {
      const note = klappNotiz.trim();
      const next = await legeMerosKlappTermin({
        klapp_am: when.toISOString(),
        notiz: note || undefined,
        color: klappFarbe,
      });
      setData(next);
      setKlappNotiz("");
      setKlappAm(jetztLokal());
      setError(null);
      const angelegt = next.klapp_termine.find(
        (termin) => Math.abs(new Date(termin.klapp_am).getTime() - when.getTime()) < 60_000,
      );
      const danach = angelegt?.zustand_danach;
      toast.success(
        danach
          ? `Klappen ist als Einsatz eingetragen. Danach stehen die Merostützen ${merosZustandLabel(danach)}.`
          : "Klappen ist als Einsatz eingetragen. Dafür fehlt noch ein vorheriger Zustand.",
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Der Termin konnte nicht gespeichert werden.");
    } finally {
      setKlappSaving(false);
    }
  }

  async function handleKlappFarbe(id: number, color: string) {
    setKlappSaving(true);
    try {
      setData(await aendereMerosKlappFarbe(id, color));
      setError(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Die Farbe konnte nicht gespeichert werden.");
    } finally {
      setKlappSaving(false);
    }
  }

  async function handleFarben(oben: string, unten: string) {
    setKlappSaving(true);
    try {
      setData(await speichereMerosFarben({ oben, unten }));
      setError(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Die Farben konnten nicht gespeichert werden.");
    } finally {
      setKlappSaving(false);
    }
  }

  async function handleKlappLoeschen(id: number) {
    if (!window.confirm("Diesen Klapp-Termin wirklich löschen?")) return;
    setKlappSaving(true);
    try {
      setData(await loescheMerosKlappTermin(id));
      setError(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Der Termin konnte nicht gelöscht werden.");
    } finally {
      setKlappSaving(false);
    }
  }

  return (
    <AppShell
      title="Meros"
      subtitle="Zustand der Merostützen erfassen"
    >
      <div className="space-y-4">
        <Alert variant="info">
          Hier wird festgehalten, ob die Merostützen gerade oben oder unten stehen. In den{" "}
          <Link to="/veranstaltungen/messe" className="font-medium text-accent hover:underline">
            Veranstaltungen
          </Link>{" "}
          steht, welcher Zustand dort benötigt wird.
        </Alert>

        {error ? <Alert variant="warning">{error}</Alert> : null}

        <Card title="Aktueller Zustand">
          {loading && !data ? (
            <Spinner className="h-5 w-5" />
          ) : data?.aktuell ? (
            <div>
              <MerosZustandBadge zustand={data.aktuell.zustand} prefix="" size="lg" />
              <p className="mt-3 text-sm text-label-secondary">
                {data.aktuell.art === "klapp" ? "Seit dem Klappen " : "Seit "}
                {data.aktuell.seit ? formatDateTime(data.aktuell.seit) : "—"}
                {data.aktuell.erfasst_von ? ` · ${data.aktuell.erfasst_von}` : ""}
              </p>
              {naechsterKlapp ? (
                <p className="mt-2 text-sm text-label-secondary">
                  Nächstes Klappen {formatDateTime(naechsterKlapp.klapp_am)}
                  {naechsterKlapp.zustand_danach
                    ? `, danach ${merosZustandLabel(naechsterKlapp.zustand_danach)}`
                    : ""}
                  .
                </p>
              ) : null}
              {data.aktuell.notiz ? (
                <p className="mt-2 whitespace-pre-line text-sm text-label">{data.aktuell.notiz}</p>
              ) : null}
            </div>
          ) : !error ? (
            <p className="text-sm text-label-secondary">
              Noch kein Zustand erfasst. Unten den aktuellen Stand eintragen.
            </p>
          ) : null}
        </Card>

        <Card
          title="Zustand erfassen"
          description="Jedes Mal eintragen, wenn die Merostützen oben oder unten stehen."
        >
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Zustand">
              {(["oben", "unten"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={zustand === value}
                  className={clsx(
                    "rounded-lg px-4 py-2 text-sm font-semibold ring-1 ring-inset transition",
                    zustand === value
                      ? "bg-accent text-label-on-accent ring-accent"
                      : "bg-card text-label ring-separator hover:bg-fill-tertiary",
                  )}
                  onClick={() => setZustand(value)}
                >
                  {merosZustandLabel(value)}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-label-secondary">Gilt seit</span>
                <input
                  type="datetime-local"
                  className="rounded border border-separator bg-card px-3 py-1.5 text-sm"
                  value={erfasstAm}
                  onChange={(event) => setErfasstAm(event.target.value)}
                />
              </label>
              <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm">
                <span className="text-label-secondary">Notiz</span>
                <input
                  type="text"
                  maxLength={500}
                  className="rounded border border-separator bg-card px-3 py-1.5 text-sm"
                  value={notiz}
                  placeholder="optional"
                  onChange={(event) => setNotiz(event.target.value)}
                />
              </label>
            </div>
            <Button onClick={() => void handleSave()} loading={saving} disabled={zustand === ""}>
              Zustand erfassen
            </Button>
          </div>
        </Card>

        <Card
          title="Klapp-Termine"
          description="Zum Termin wird der Zustand gegenüber dem vorherigen umgekehrt und als eigener Einsatz in die Zeile Meros geschrieben."
        >
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-label-secondary">Zustand OBEN</span>
                <FarbeWahl
                  value={data?.farben?.oben || FARBE_OBEN}
                  disabled={klappSaving}
                  onChange={(color) =>
                    void handleFarben(color, data?.farben?.unten || FARBE_UNTEN)
                  }
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-label-secondary">Zustand UNTEN</span>
                <FarbeWahl
                  value={data?.farben?.unten || FARBE_UNTEN}
                  disabled={klappSaving}
                  onChange={(color) =>
                    void handleFarben(data?.farben?.oben || FARBE_OBEN, color)
                  }
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-label-secondary">Klappen am</span>
                <input
                  type="datetime-local"
                  className="rounded border border-separator bg-card px-3 py-1.5 text-sm"
                  value={klappAm}
                  onChange={(event) => setKlappAm(event.target.value)}
                />
              </label>
              <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm">
                <span className="text-label-secondary">Notiz</span>
                <input
                  type="text"
                  maxLength={500}
                  className="rounded border border-separator bg-card px-3 py-1.5 text-sm"
                  value={klappNotiz}
                  placeholder="optional"
                  onChange={(event) => setKlappNotiz(event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-label-secondary">Farbe des Einsatzes</span>
                <FarbeWahl value={klappFarbe} disabled={klappSaving} onChange={setKlappFarbe} />
              </label>
            </div>
            <Button onClick={() => void handleKlapp()} loading={klappSaving}>
              Termin eintragen
            </Button>
            {(data?.klapp_termine.length || 0) === 0 ? (
              <p className="text-sm text-label-secondary">Noch kein Klapp-Termin.</p>
            ) : (
              <ul className="divide-y divide-separator">
                {data?.klapp_termine.map((termin) => {
                  const vorbei = new Date(termin.klapp_am).getTime() < Date.now();
                  return (
                    <li
                      key={termin.id}
                      className={clsx(
                        "flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0 last:pb-0",
                        vorbei && "opacity-70",
                      )}
                    >
                      <div>
                        <p className="text-sm font-medium text-label">{formatDateTime(termin.klapp_am)}</p>
                        {termin.notiz ? <p className="mt-1 text-sm text-label-secondary">{termin.notiz}</p> : null}
                        <p className="mt-1 text-xs text-label-secondary">
                          {termin.zustand_davor && termin.zustand_danach
                            ? `${merosZustandLabel(termin.zustand_davor)} wird ${merosZustandLabel(termin.zustand_danach)}`
                            : "Vorheriger Zustand fehlt noch"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <FarbeWahl
                          value={termin.color || FARBE_OBEN}
                          disabled={klappSaving}
                          onChange={(color) => void handleKlappFarbe(termin.id, color)}
                        />
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={klappSaving}
                        onClick={() => void handleKlappLoeschen(termin.id)}
                      >
                        Löschen
                      </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>

        <Card
          title="Benötigt in Veranstaltungen"
          description="Aus der Aufgabe der aktuellen Regiepläne."
        >
          {loading && !data ? (
            <Spinner className="h-5 w-5" />
          ) : error ? null : anstehend.length === 0 ? (
            <p className="text-sm text-label-secondary">
              In den gelesenen Veranstaltungen ist noch kein Merostützen-Zustand genannt.
            </p>
          ) : (
            <BedarfListe items={anstehend} aktuell={aktuell} />
          )}
          {vergangen.length > 0 ? (
            <details className="mt-4 border-t border-separator pt-3">
              <summary className="cursor-pointer text-sm font-medium text-label-secondary">
                Vergangene Veranstaltungen ({vergangen.length})
              </summary>
              <div className="pt-3">
                <BedarfListe items={vergangen} aktuell={null} />
              </div>
            </details>
          ) : null}
        </Card>

        <Card title="Erfassungen">
          {loading && !data ? (
            <Spinner className="h-5 w-5" />
          ) : error ? null : (data?.historie.length || 0) === 0 ? (
            <p className="text-sm text-label-secondary">Noch keine Erfassung.</p>
          ) : (
            <ul className="divide-y divide-separator">
              {data?.historie.map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div>
                    <MerosZustandBadge zustand={entry.zustand} prefix="" />
                    {entry.notiz ? <p className="mt-2 text-sm text-label">{entry.notiz}</p> : null}
                  </div>
                  <p className="text-sm text-label-secondary">
                    {entry.erfasst_am ? formatDateTime(entry.erfasst_am) : "—"}
                    {entry.erfasst_von ? ` · ${entry.erfasst_von}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
